package pl.decks.personal.pages.ievp.sections.experience

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.CSSFloat
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.border
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.dom.Span
import pl.decks.personal.components.graphics.ExuraLogo
import pl.decks.personal.components.widgets.LeadingIconRow
import pl.decks.personal.components.widgets.TrailingIconRow
import pl.decks.personal.components.widgets.disclaimer.DisclaimerBox
import pl.decks.personal.components.widgets.responsivegrid.ResponsiveGrid
import pl.decks.personal.components.widgets.responsivegrid.ResponsiveGridOption
import pl.decks.personal.components.widgets.timeline.*
import pl.decks.personal.extensions.responsiveValue
import pl.decks.personal.extensions.toSitePalette
import pl.decks.personal.pages.ievp.IEVPJourney
import pl.decks.personal.pages.ievp.IEVPSection
import pl.decks.personal.pages.ievp.sections.experience.ExperienceSectionStylesheet.Breakdown
import pl.decks.personal.pages.ievp.sections.experience.ExperienceSectionStylesheet.BreakdownPanel
import pl.decks.personal.pages.ievp.sections.experience.ExperienceSectionStylesheet.CircleSelector
import pl.decks.personal.pages.ievp.sections.experience.ExperienceSectionStylesheet.Vars
import pl.decks.personal.styles.FontTypography
import pl.decks.personal.styles.SiteLayout
import pl.decks.posthog.client.PostHogClient

private sealed interface HighlightStyle {
    data object Selected : HighlightStyle
    data object Hovered : HighlightStyle
}

private fun ExperienceEntry.toHighlight(style: HighlightStyle): TimelineHighlight =
    TimelineHighlight(
        span = this.span,
        isOverridingMarker = true,
        monthRenderer = MonthRenderer(
            marker = { ym ->
                val color = this.color(ColorMode.current).toRgb()
                when (style) {
                    HighlightStyle.Selected -> SpacedBarMonthMarker(color)

                    HighlightStyle.Hovered -> {
                        val alpha = 0.8f - 0.6f * (ym - this.span.from) / this.span.span
                        MonthMarkers.Circle(Modifier.backgroundColor(color.copyf(alpha = alpha)))
                    }
                }
            },
            label = { ym ->
                if (ym == this.span.from || ym == this.span.to) SpanText(
                    ym.month.shortLabel,
                    modifier = Modifier.color(this.color(ColorMode.current))
                )
            },
        )
    )

private fun timelineBounds(
    vararg spans: YearMonth.Range,
    addMonthsLeft: Int = 0,
    addMonthsRight: Int = 0,
): YearMonth.Range = YearMonth.Range(
    from = spans.minOf { it.from } - addMonthsLeft,
    to = spans.maxOf { it.to } + addMonthsRight,
)


@Composable
fun ExperienceSectionContent(sectionReached: Boolean) = Column(
    modifier = ExperienceSectionStylesheet.Section.toModifier(),
    horizontalAlignment = Alignment.CenterHorizontally,
    verticalArrangement = Arrangement.Center,
) {
    var selectedEntry by remember { mutableStateOf(ExperienceEntry.entries.last()) }
    var hoveredEntry by remember { mutableStateOf<ExperienceEntry?>(null) }

    DisclaimerBox(
        modifier = Modifier
            .fillMaxWidth()
            .fontSize(FontTypography.Size.Small)
            .onClick { PostHogClient.capture(IEVPJourney.DisclaimerOpened(IEVPSection.EXPERIENCE)) },
    ) {
        SpanText("These are personal insights and lessons taken. I do not feel like speaking for others, who cannot correct for their point of view.")
    }
    ExperienceTimeline(selectedEntry, hoveredEntry)
    ExperienceEntriesSelector(
        selectedEntry,
        onSelect = { selectedEntry = it },
        onHover = { hoveredEntry = it },
        onHoverEnd = { hoveredEntry = null },
    )
    ExperienceBreakdown(
        selectedEntry,
        sectionReached = sectionReached,
        onEntrySwitched = { selectedEntry = it },
        onEntryHovered = { hoveredEntry = it },
        onEntryHoverEnd = { hoveredEntry = null },
    )
}

@OptIn(DelicateApi::class)
@Composable
private fun ExperienceTimeline(
    selectedEntry: ExperienceEntry,
    hoveredEntry: ExperienceEntry?,
) {
    val breakpoint = rememberBreakpoint()
    val timelineRange = remember(selectedEntry, hoveredEntry, breakpoint) {
        val rangeEntries = if (breakpoint <= Breakpoint.MD)
            listOfNotNull(selectedEntry, hoveredEntry?.takeIf { hoveredEntry != selectedEntry })
        else
            ExperienceEntry.entries

        timelineBounds(
            *rangeEntries.map { it.span }.toTypedArray(),
            addMonthsLeft = 1,
            addMonthsRight = 3
        )
    }
    val highlights = remember(selectedEntry, hoveredEntry) {
        listOfNotNull(
            selectedEntry.toHighlight(HighlightStyle.Selected),
            hoveredEntry?.takeIf { it != selectedEntry }?.toHighlight(HighlightStyle.Hovered),
        )
    }
    val monthRenderer = rememberHighlightRenderer(
        timelineRange,
        highlights,
        defaultRenderer = MonthRenderer(
            marker = { SpacedBarMonthMarker(ColorMode.current.toPalette().border) },
            label = {},
        )
    )
    Box(
        modifier = ExperienceSectionStylesheet.Timeline.toModifier(),
        contentAlignment = Alignment.Center,
    ) {
        YearlySplitRangeTimeline(
            range = timelineRange,
            isShowingYearLabel = true,
            yearLabelAlignment = Alignment.TopStart,
            monthRenderer = monthRenderer,
            modifier = Modifier.fillMaxWidth(),
        )
    }

}

@Composable
private fun ExperienceEntriesSelector(
    selectedEntry: ExperienceEntry,
    onSelect: (ExperienceEntry) -> Unit,
    onHover: (ExperienceEntry) -> Unit,
    onHoverEnd: () -> Unit,
) = Row(
    modifier = CircleSelector.Options.toModifier(),
    verticalAlignment = Alignment.CenterVertically,
    horizontalArrangement = Arrangement.Center,
) {
    ExperienceEntry.entries.forEach { entry ->
        ExperienceSelectionOption(
            entry = entry,
            isSelected = selectedEntry == entry,
            onSelect = { onSelect(entry) },
            onHover = { onHover(entry) },
            onHoverEnd = onHoverEnd,
        )
    }
}

@Composable
private fun ExperienceSelectionOption(
    entry: ExperienceEntry,
    isSelected: Boolean,
    onSelect: () -> Unit,
    onHover: () -> Unit,
    onHoverEnd: () -> Unit,
) = Box(
    modifier = CircleSelector.Style
        .toModifier(if (isSelected) CircleSelector.SelectedVariant else null)
        .setVariable(Vars.timelineOptionBackgroundColor, entry.logoBgColor(ColorMode.current))
        .setVariable(Vars.timelineOptionAccentColor, entry.color(ColorMode.current))
        .cursor(if (!isSelected) Cursor.Pointer else Cursor.Default)
        .onClick { if (!isSelected) onSelect() }
        .onMouseEnter { onHover() }
        .onMouseLeave { onHoverEnd() }
        .onTouchStart { onHover() }
        .onTouchEnd { onHoverEnd() }
        .onTouchCancel { onHoverEnd() }
) {
    entry.logo()
}


@Composable
private fun ExperienceBreakdown(
    selectedEntry: ExperienceEntry,
    sectionReached: Boolean,
    onEntrySwitched: (ExperienceEntry) -> Unit,
    onEntryHovered: (ExperienceEntry) -> Unit,
    onEntryHoverEnd: () -> Unit,
) = Column(
    modifier = Breakdown.Container.toModifier()
        .setVariable(Vars.experienceBreakdownColor, selectedEntry.color(ColorMode.current)),
    horizontalAlignment = Alignment.CenterHorizontally,
) {
    Box(contentAlignment = Alignment.CenterStart) {
        ExperienceEntry.entries.forEach { entry ->
            ExperienceCompanyHeader(
                entry = entry,
                isVisible = entry == selectedEntry,
            )
        }
    }

    ExperienceBreakdownInformation(
        selectedEntry,
        sectionReached,
        onEntrySwitched,
        onEntryHovered,
        onEntryHoverEnd,
    )
}

@Composable
private fun ExperienceCompanyHeader(
    entry: ExperienceEntry,
    isVisible: Boolean,
) = Row(
    modifier = Breakdown.CompanyHeader.toModifier()
        .setVariable(
            Vars.timelineOptionBackgroundColor,
            entry.logoBgColor(ColorMode.current) ?: ColorMode.current.toSitePalette().surface,
        )
        .opacity(if (isVisible) 1 else 0),
    verticalAlignment = Alignment.CenterVertically,
) {
    Box(
        modifier = Breakdown.CompanyLogoContainer.toModifier(),
        contentAlignment = Alignment.Center,
    ) {
        entry.logo()
    }

    Column(horizontalAlignment = Alignment.End) {
        SpanText(entry.company, modifier = Breakdown.CompanyName.toModifier())
        HorizontalDivider(Breakdown.Divider.toModifier())
        TrailingIconRow(
            icon = { FaMapPin() },
            modifier = Breakdown.Underwrite.toModifier(),
        ) {
            SpanText(entry.location)
        }
    }
}

@Composable
private fun ExperienceBreakdownInformation(
    selectedEntry: ExperienceEntry,
    sectionReached: Boolean,
    onEntrySwitched: (ExperienceEntry) -> Unit,
    onEntryHovered: (ExperienceEntry) -> Unit,
    onEntryHoverEnd: () -> Unit,
) = Box(Breakdown.InformationContainer.toModifier()) {
    var selectedPanel by remember(selectedEntry) { mutableStateOf(ExperienceBreakdownPanel.entries.first()) }

    LaunchedEffect(selectedEntry, selectedPanel, sectionReached) {
        if (sectionReached)
            PostHogClient.capture(IEVPJourney.ExperiencePanelPresented(selectedEntry, selectedPanel))
    }
    key(selectedEntry) {
        Box(Breakdown.InformationContainerFlavorBG.toModifier().zIndex(0))
    }
    Column(Modifier.fillMaxSize().zIndex(1)) {
        ExperiencePanelSelector(selectedPanel, onSelect = { selectedPanel = it })
        when (selectedPanel) {
            ExperienceBreakdownPanel.ROLE_PROFILE ->
                ExperienceRoleProfilePanel(selectedEntry.profile) { selectedEntry.roleForeword() }

            ExperienceBreakdownPanel.WENT_WELL ->
                OrnateParagraphsPanel(
                    ornate = { FaCircleCheck(modifier = Modifier.color(Vars.experienceBreakdownColor.value())) },
                    entries = selectedEntry.wentWellEntries(),
                )

            ExperienceBreakdownPanel.NOT_SO_WELL ->
                OrnateParagraphsPanel(
                    ornate = { FaCircleXmark(modifier = Modifier.color(Vars.experienceBreakdownColor.value())) },
                    entries = selectedEntry.notSoWellEntries(),
                )
        }
        ExperienceBreakdownNavigation(
            selectedEntry,
            onEntrySwitched,
            onEntryHovered,
            onEntryHoverEnd,
        )
    }
}


@Composable
private fun ExperienceRoleProfilePanel(
    roleProfile: ExperienceEntry.RoleProfile,
    foreword: @Composable () -> Unit,
) = Box(
    modifier = BreakdownPanel.Content.toModifier()
        .setVariable(Vars.underwriteColor, ColorMode.current.toSitePalette().accents.primary),
    contentAlignment = Alignment.Center,
) {
    Column(Modifier.fillMaxWidth().gap(SiteLayout.Space.Small), horizontalAlignment = Alignment.CenterHorizontally) {
        ExperienceRoleProfileEntry("role title") {
            SpanText(roleProfile.roleTitle, modifier = BreakdownPanel.PrimaryText.toModifier())
        }
        Box(BreakdownPanel.Foreword.toModifier()) {
            foreword()
        }
        ExperienceRoleProfileEntrySecondary("market", roleProfile.market)
        ExperienceRoleProfileEntrySecondary("target clients", roleProfile.clients)
        ExperienceRoleProfileEntrySecondary("department", roleProfile.department)
        ExperienceRoleProfileEntrySecondary("aim", roleProfile.responsibility)
        ExperienceRoleProfileEntrySecondary("direct stakeholders", roleProfile.stakeholders)
    }
}

@Composable
private fun OrnateParagraphsPanel(
    ornate: @Composable () -> Unit,
    entries: List<@Composable () -> Unit>,
) = Box(
    modifier = BreakdownPanel.Content.toModifier(),
    contentAlignment = Alignment.Center,
) {
    Column(Modifier.fillMaxWidth().gap(SiteLayout.Space.Small)) {
        entries.forEach { entry ->
            OrnateParagraph(
                ornate = ornate,
                modifier = Modifier.fillMaxWidth().textAlign(TextAlign.Justify),
            ) {
                entry()
            }
        }
    }
}

@Composable
private fun OrnateParagraph(
    ornate: (@Composable () -> Unit),
    modifier: Modifier = Modifier,
    content: @Composable () -> Unit,
) = Span(attrs = modifier.toAttrs()) {
    Span(
        attrs = Modifier
            .fontSize(2.em)
            .lineHeight(1)
            .float(CSSFloat.Left)
            .margin(top = 0.2.em, right = 0.2.em)
            .toAttrs(),
    ) { ornate() }
    content()
}

@Composable
private fun ExperienceRoleProfileEntrySecondary(
    underwrite: String,
    text: String,
) = ExperienceRoleProfileEntry(underwrite, Alignment.Start, Modifier.fillMaxWidth()) {
    SpanText(text, modifier = BreakdownPanel.SecondaryText.toModifier())
}

@Composable
private fun ExperienceRoleProfileEntry(
    underwrite: String,
    alignment: Alignment.Horizontal = Alignment.CenterHorizontally,
    modifier: Modifier = Modifier,
    content: @Composable () -> Unit,
) = Column(
    modifier = modifier,
    horizontalAlignment = alignment,
) {
    content()
    HorizontalDivider(Breakdown.Divider.toModifier())
    SpanText(underwrite, modifier = Breakdown.Underwrite.toModifier())
}

@Composable
private fun ExperiencePanelSelector(
    selectedPanel: ExperienceBreakdownPanel,
    onSelect: (ExperienceBreakdownPanel) -> Unit,
) = Box(Modifier.fillMaxWidth().padding(SiteLayout.Space.XSmall)) {
    ResponsiveGrid(
        options = ExperienceBreakdownPanel.entries.map { ResponsiveGridOption(title = it.optionText) },
        selectedOption = ExperienceBreakdownPanel.entries.indexOf(selectedPanel),
        numColumns = responsiveValue(3),
        onOptionSelected = { onSelect(ExperienceBreakdownPanel.entries[it]) },
        hoverGrow = responsiveValue(1.3f),
        modifier = Modifier.fillMaxWidth(),
    )
}

@Composable
private fun ExperienceBreakdownNavigation(
    selectedEntry: ExperienceEntry,
    onEntrySwitched: (ExperienceEntry) -> Unit,
    onEntryHovered: (ExperienceEntry) -> Unit,
    onEntryHoverEnd: () -> Unit,
) = Row(Breakdown.BottomNavigation.toModifier()) {
    val entryPos = ExperienceEntry.entries.indexOf(selectedEntry)
    val prevEntry = ExperienceEntry.entries.getOrNull(entryPos - 1)
    val nextEntry = ExperienceEntry.entries.getOrNull(entryPos + 1)
    ExperienceBreakdownButton(
        onClick = { prevEntry?.let { onEntrySwitched(it) } },
        onHover = { prevEntry?.let { onEntryHovered(it) } },
        onHoverEnd = onEntryHoverEnd,
        isActive = prevEntry != null,
        modifier = Modifier.weight(1f),
    ) {
        LeadingIconRow({ FaAnglesLeft() }) { SpanText("Previous") }
    }
    ExperienceBreakdownButton(
        onClick = { nextEntry?.let { onEntrySwitched(it) } },
        onHover = { nextEntry?.let { onEntryHovered(it) } },
        onHoverEnd = onEntryHoverEnd,
        isActive = nextEntry != null,
        modifier = Modifier.weight(1f),
    ) {
        TrailingIconRow({ FaAnglesRight() }) { SpanText("Next") }
    }
}

@Composable
private fun ExperienceBreakdownButton(
    onClick: () -> Unit,
    onHover: () -> Unit,
    onHoverEnd: () -> Unit,
    isActive: Boolean,
    modifier: Modifier = Modifier,
    content: @Composable () -> Unit,
) = Box(
    modifier = Breakdown.NavigationButton
        .toModifier(if (isActive) Breakdown.ActiveNavigationButtonVariant else null)
        .thenIf(isActive) {
            Modifier
                .cursor(if (isActive) Cursor.Pointer else Cursor.Default)
                .onClick { if (isActive) onClick().also { onHoverEnd() } }
                .onMouseEnter { onHover() }
                .onMouseLeave { onHoverEnd() }
                .onTouchStart { onHover() }
                .onTouchEnd { onHoverEnd() }
                .onTouchCancel { onHoverEnd() }
        }
        .then(modifier),
    contentAlignment = Alignment.Center,
) {
    Box(
        modifier = Modifier.padding(SiteLayout.Space.Small).opacity(if (isActive) 1f else 0f),
        contentAlignment = Alignment.Center,
    ) {
        content()
    }
}

@Composable
private fun SpacedBarMonthMarker(color: Color) = MonthMarkers.RoundedBar(
    modifier = Modifier.width(90.percent).backgroundColor(color)
)


@Composable
private fun ExperienceEntry.logo() {
    if (this == ExperienceEntry.EXURA) ExuraLogo(
        modifier = Modifier.color(ColorMode.current.toSitePalette().accents.secondary).fillMaxSize()
    )
    else Image(
        src = when (this) {
            ExperienceEntry.CREDIT_AGRICOLE -> "/images/evp/logo/credit_agricole.svg"
            ExperienceEntry.NOKIA -> "/images/evp/logo/nokia.svg"
            ExperienceEntry.PEAK_DATA -> "/images/evp/logo/peakdata.svg"
            ExperienceEntry.WILLIAM_HILL -> "/images/evp/logo/william_hill.svg"
            ExperienceEntry.AXIOM_HEALTH -> "/images/evp/logo/axiomhealth.png"
        },
        modifier = Modifier.fillMaxSize().objectFit(ObjectFit.Contain),
    )

}

@Composable
private fun ExperienceEntry.roleForeword() = Span {
    when (this@roleForeword) {
        ExperienceEntry.CREDIT_AGRICOLE -> {
            SpanText("While I've been working seasonally since I was 14 and during studies to meet my ends, this was an ")
            SpanText("internship", modifier = BreakdownPanel.TextEmphasis.toModifier())
            SpanText(" that opened me to the world of data.")
        }

        ExperienceEntry.NOKIA -> {
            SpanText("Here is where I was first given real ")
            SpanText("responsibility", modifier = BreakdownPanel.TextEmphasis.toModifier())
            SpanText(" and ")
            SpanText("ownership. ", modifier = BreakdownPanel.TextEmphasis.toModifier())
            SpanText("Turned out I love it.")
        }

        ExperienceEntry.PEAK_DATA -> {
            SpanText("Ownership is great, but it comes nowhere near being ")
            SpanText("valuable", modifier = BreakdownPanel.TextEmphasis.toModifier())
            SpanText(". I learned how it feels to be more than just a nice addition, rather a vital point. ")
            SpanText("What it means to be ")
            SpanText("impactful", modifier = BreakdownPanel.TextEmphasis.toModifier())
            SpanText(".")
        }

        ExperienceEntry.WILLIAM_HILL -> {
            SpanText("It was the first place, where I could learn not only by myself for the sake of company. ")
            SpanText("I met a professional data-oriented environment and could elevate by the ")
            SpanText("team", modifier = BreakdownPanel.TextEmphasis.toModifier())
            SpanText(" and ")
            SpanText("collective intelligence", modifier = BreakdownPanel.TextEmphasis.toModifier())
            SpanText(".")
        }

        ExperienceEntry.AXIOM_HEALTH -> {
            SpanText("By the time I joined, I earned my ")
            SpanText("rights to be confident", modifier = BreakdownPanel.TextEmphasis.toModifier())
            SpanText(". It was refreshing to not feel the slightest hint of impostor syndrome. ")
            SpanText("I knew that whatever I came to do, I'd manage. ")
        }

        ExperienceEntry.EXURA -> {
            SpanText("A leap of faith with no haystack underneath. ")
            SpanText("Definitely the biggest risk I've ever taken and ")
            SpanText("not a single second of regret", modifier = BreakdownPanel.TextEmphasis.toModifier())
            SpanText(".")
        }
    }
}

@Composable
private fun ExperienceEntry.wentWellEntries(): List<@Composable () -> Unit> = buildList {
    when (this@wentWellEntries) {
        ExperienceEntry.CREDIT_AGRICOLE -> {
            add(
                {
                    SpanText("Actually got to apply learnings from my studies to a real-world problem while ")
                    SpanText("improving my Python skills", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(".")
                }
            )
            add(
                {
                    SpanText("Created a customer behaviour segmentation pipeline using ")
                    SpanText("stochastic Machine Learning models", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(" that presented results via interactive notebook.")
                }
            )
            add(
                {
                    SpanText("Both validated and invalidated a few hypotheses related to consumer behaviour, learning that ")
                    SpanText("negative result can be valuable too", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(".")
                }
            )
        }

        ExperienceEntry.NOKIA -> {
            add(
                {
                    SpanText("Took ownership of BTS configuration optimizer, which I migrated from R to Python for ")
                    SpanText("readability and easier maintenance", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(".")
                }
            )
            add(
                {
                    SpanText("Heavily extended it, working ")
                    SpanText("directly with the stakeholders", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(" - added familiar end-user interfaces like Excel integration, improved results, acceptance ratio and usage.")
                }
            )
            add(
                {
                    SpanText("Learned to present the results in the language of ")
                    SpanText("benefits, metrics and monetary value", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(". I can say that the tool has saved the company a bit ")
                    SpanText("over $1M in revenue", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(", making the ")
                    SpanText("ROI", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(" of my employment ")
                    SpanText("around 900%", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(".")
                }
            )
            add(
                {
                    SpanText("Over time I introduced DevOps practices, like  ")
                    SpanText("version control, testing, CI/CD...", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(" Managed to ")
                    SpanText("stabilize the results and automate pretty much every step", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(" of the process, from PDF specification parsing to the ETL and presentation.")
                }
            )
        }

        ExperienceEntry.PEAK_DATA -> {
            add(
                {
                    SpanText("I was initially hired as a ")
                    SpanText("Data Scientist", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(". With a quick increase in demand for the product, it became evident that the team lacked skills required for ")
                    SpanText("large volume processing and flow optimization", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(". I took the responsibility and grew to be a ")
                    SpanText("Data Engineer", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(" among Scientists.")
                }
            )
            add(
                {
                    SpanText("The role was versatile. ")
                    SpanText("I got to contribute to ")
                    SpanText("strategic meetings", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(". Spoke with the ")
                    SpanText("clients", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(". Built the ")
                    SpanText("infrastructure", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(", created ")
                    SpanText("internal frameworks", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(" and took care of the ")
                    SpanText("product quality", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(".")
                }
            )
            add(
                {
                    SpanText("Collectively as a Data team we increased the throughput of a custom-built solution thirty-fold - ")
                    SpanText("ten times the volume in one-third of the time", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(".")
                }
            )
            add(
                {
                    SpanText("Last six months I was promoted to a ")
                    SpanText("Data Team Lead", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(". While I still worked on crucial workflows, my responsibilities shifted towards ")
                    SpanText("design and practices distribution", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(". I've initiated the process of migrating the solution to a cloud environment, scaled the team ")
                    SpanText("from 3 to 6 people", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(" and cut the time needed to spin up a new data module from scratch in half. ")
                }
            )
        }

        ExperienceEntry.WILLIAM_HILL -> {
            add(
                {
                    SpanText("Initially was working on creating pipelines that extracted and processed gambling behaviour data. ")
                    SpanText("I created three of those in the first three months. ")
                    SpanText("Alongside, I ")
                    SpanText("refactored and extended", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(" parts of ")
                    SpanText("shared libraries", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(".")
                }
            )
            add(
                {
                    SpanText("The performance earned me a switch to a different department and some ")
                    SpanText("freedom in the task selection", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(". Many people in the data field struggle with writing ")
                    SpanText("production grade code", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(" and it turns out I am quite fine with that. ")
                    SpanText("The new role was a clash of ")
                    SpanText("Data Engineering", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(" and ")
                    SpanText("DevOps", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(". I've lead the initiatives of ")
                    SpanText("reducing tech-debt and simplifying tech-stack", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(".")
                }
            )
            add(
                {
                    SpanText("Decomposed ")
                    SpanText("DataBricks and Kafka", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(", which were ")
                    SpanText("tried out as prototypes", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(". Never chosen as a permanent solution, their results were ")
                    SpanText("still used by downstream processes", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(".")
                }
            )
            add(
                {
                    SpanText("Remade a development CI/CD pipeline for the Unified Data Platform, ")
                    SpanText("cutting its build time from 20 to 3 minutes", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(".")
                }
            )
        }

        ExperienceEntry.AXIOM_HEALTH -> {

            add(
                {
                    SpanText("Inherited an Amazon EMR data infrastructure and scaled it from ")
                    SpanText("1 TB to 20 TB", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(" of regular throughput. ")
                    SpanText("Shortly after, the need rose to ")
                    SpanText("100 TB", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(".")
                }
            )
            add(
                {
                    SpanText("The company was struggling. ")
                    SpanText("Third month in, I was more knowledgeable with the state of affairs and could pinpoint the ")
                    SpanText("root problem", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(". ")
                    SpanText("The company operated on two fronts - selling custom curated data for insights and trying to build a product out of it. ")
                    SpanText("The data infrastructure was shared: suitable for automated analytical dashboard and burden for iterative insights discovery. ")
                    SpanText("Buried in the clients' work, observability issues and unfriendly tools, the Data Scientists ")
                    SpanText("couldn't invest enough time", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(" to make a quality product. ")
                }
            )
            add(
                {
                    SpanText("Managed to ")
                    SpanText("build a unified front", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(" and convince the leadership to invest time into building a new data solution. ")
                }
            )
            add(
                {
                    SpanText("Maintained the old solution while ")
                    SpanText("driving the change", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(". ")
                    SpanText("I was responsible for the whole process, from ")
                    SpanText("requirements specification, through market research, prototyping, measuring, up to choice presentation", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(". ")
                }
            )
            add(
                {
                    SpanText("Migrated the whole stack in four months. Was ")
                    SpanText("main developer, architect and administrator", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(" of a ")
                    SpanText("Data Lake Platform on Databricks", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(", code name ")
                    SpanText("Data Current", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(".")
                }
            )
            add(
                {
                    SpanText("Taught and shared good practices related to DataBricks and ")
                    SpanText("PySpark configuration and optimization", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(". Became a proud, founding author of ")
                    SpanText("Current's PySpark Practical Playbook", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(". ")
                }
            )
            add(
                {
                    SpanText("The change has saved ")
                    SpanText("$50k in monthly expenses", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(" and freed ")
                    SpanText("one third of the Data Team's time", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(".")
                }
            )
        }

        ExperienceEntry.EXURA -> {
            add(
                {
                    SpanText("Strongly kept the iterative approach and ")
                    SpanText("reached for feedback fast", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(". Started with ")
                    SpanText("research, then meetings, PDF presentations, mockups, wireframes", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(" and only after that ")
                    SpanText("prototypes", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(".")
                }
            )
            add(
                {
                    SpanText("Wasn't clingy to our solutions, internalising ")
                    SpanText("problem-oriented thinking", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(" and easily adjusting every time we understood something better.")
                }
            )
            add(
                {
                    SpanText("Managed to create and ")
                    SpanText("pilot an MVP", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(" that was quite complex from the ground up.")
                }
            )
            add(
                {
                    SpanText("Leveled up the ")
                    SpanText("business environment understanding and soft skills", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(", while significantly expanding on ")
                    SpanText("product design, customer research, problem mapping and full-stack tech knowledge", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(".")
                }
            )
        }
    }
}

@Composable
private fun ExperienceEntry.notSoWellEntries(): List<@Composable () -> Unit> = buildList {
    when (this@notSoWellEntries) {
        ExperienceEntry.CREDIT_AGRICOLE -> {
            add(
                {
                    SpanText("Was too clingy to the known toolstack and didn't ")
                    SpanText("pick up enough new skills", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(".")
                }
            )
            add(
                {
                    SpanText("Had no idea how to make the results of my work ")
                    SpanText("more actionable to the stakeholders", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(".")
                }
            )
            add(
                {
                    SpanText("Instead of ")
                    SpanText("understanding where the regulations come from", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(", I brushed them off as mere annoyances.")
                }
            )
            add(
                {
                    SpanText("Though I was offered to turn the internship into a position, the offer was way below market and I didn't know ")
                    SpanText("how to negotiate", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(" or argue for a better one.")
                }
            )
        }

        ExperienceEntry.NOKIA -> {
            add(
                {
                    SpanText("Managed to work, study and also teach a course at the same time, but that ")
                    SpanText("burned me out", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(" quite quickly.")
                }
            )
            add(
                {
                    SpanText("Underestimated the value of domain knowledge", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(" and would sometimes make uninformed, naïve decisions which resulted in temporarily worse results.")
                }
            )
            add(
                {
                    SpanText("When I automated most of my work, I got bored. ")
                    SpanText("Instead of fighting for ")
                    SpanText("new avenues and exciting projects", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(", for some time I just pretended to appear productive. ")
                    SpanText("I fell for a comfort trap and I am ashamed of it.")
                }
            )
        }

        ExperienceEntry.PEAK_DATA -> {
            add(
                {
                    SpanText("Took way too long to understand that the inherited custom infrastructure for data processing made us ")
                    SpanText("reinvent a lot of wheels", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(" and was impeding the ")
                    SpanText("work actually valuable", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(" to the customers.")
                }
            )
            add(
                {
                    SpanText("I was over-promoted and didn't have the ")
                    SpanText("skill-set and knowledge", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(" required to handle all the challenges the company was facing. ")
                    SpanText("No one had, so there was ")
                    SpanText("nobody to learn from", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(". It was communicated and the founders were understanding, doing their best to ")
                    SpanText("hire the right people", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(".")
                }
            )
            add(
                {
                    SpanText("Nevertheless, I felt I stagnated and wasn't progressing, so I ")
                    SpanText("longed to change the environment", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(". I was too hasty in my decision, ")
                    SpanText("not understanding that good hiring takes time", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(", and took a position with a ")
                    SpanText("salary lower by one third", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(", just to make sure I keep learning. ")
                    SpanText("I even took pride in it for a while.")
                }
            )
        }

        ExperienceEntry.WILLIAM_HILL -> {
            add(
                {
                    SpanText("Couldn't bear lengthy acceptance and review cycles.")
                }
            )
            add(
                {
                    SpanText("Tried fixing processes and people problems with technical solutions.")
                }
            )
            add(
                {
                    SpanText("Learned I suck at corporate politics.")
                }
            )
            add(
                {
                    SpanText("Working in gambling felt morally draining. Clients were numbers and the aim was to prey on their weaknesses.")
                }
            )
            add(
                {
                    SpanText("Worked in a department ")
                    SpanText("isolated from the daily operations", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(" of the company, with too ")
                    SpanText("little agency", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(".")
                }
            )
        }

        ExperienceEntry.AXIOM_HEALTH -> {
            add(
                {
                    SpanText("Too caught up in the hurdles and struggles of the team, I didn't ")
                    SpanText("push back hard enough", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(" on decisions related to ingestion of new data sources. ")
                    SpanText("We already had too little workforce to handle the ones we had well.")
                }
            )
            add(
                {
                    SpanText("Focused too much on the data and infrastructure and too little on the ")
                    SpanText("product UI and UX", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(". ")
                    SpanText("And that was how the product clients interacted with our results.")
                }
            )
            add(
                {
                    SpanText("Didn't fight for enough direct meetings with clients and users, too often ")
                    SpanText("receiving their feedback from the secondhand", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(".")
                }
            )
        }

        ExperienceEntry.EXURA -> {
            add(
                {
                    SpanText("Definitely ")
                    SpanText("underestimated the amount of money, work and knowledge", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(" that was required to spin up the company.")
                }
            )
            add(
                {
                    SpanText("Thought my saving will be enough to enter the path and ")
                    SpanText("did not secure any personal recurring income", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(".")
                }
            )
            add(
                {
                    SpanText("Learning along the way, oftentimes got lost and ")
                    SpanText("put resources into aspects of secondary importance", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(".")
                }
            )
            add(
                {
                    SpanText("Made too many decisions on \"it feels like a good idea\", without ")
                    SpanText("factual data and proof", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(".")
                }
            )
            add(
                {
                    SpanText("Tried to ")
                    SpanText("chase too many opportunities", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(", instead of meticulously following a drafted plan. ")
                    SpanText("Drowned in options and ")
                    SpanText("struggled to prioritize", modifier = BreakdownPanel.TextEmphasis.toModifier())
                    SpanText(".")
                }
            )
        }
    }
}
